/**
 * Storefront sharing & Open Graph helpers.
 *
 * Source UNIQUE de l'URL produit partagée (deep-link path durable) — réutilisable par
 * le partage Canal A (wa.me / partage natif) ET le futur dispatch WABA (Canal B), sans
 * refonte. Cf. dissidence :
 * docs/dissidence/whatsapp-product-channels/A2-product-share-og-deeplink-dissidence.md
 */

/** URL canonique publique d'une vitrine. */
export function getStorefrontUrl(baseUrl: string, shortLink: string): string {
  return `${baseUrl}/s/${shortLink}`
}

/**
 * Deep-link DURABLE d'un produit — PATH segment, jamais query param.
 *
 * Le path est choisi pour la robustesse du scraper Open Graph WhatsApp ET pour survivre
 * aux proxys HTTP transparents (réseaux Orange/MTN CI) qui peuvent normaliser une URL en
 * strippant la query string avant de servir leur cache (casserait l'unicité de l'aperçu).
 * Les liens partagés sur WhatsApp étant immortels, ce format est un contrat public stable.
 *
 * ⚠️ Depuis V0 (dossier 20 § 10.2), le bouton de partage de la vitrine AJOUTE `?via=partage` à ce lien
 * (`avecVia`) : le chemin, qui porte le produit et son aperçu, ne change pas ; un proxy qui retire la
 * requête ne perd que la source (la visite arrive alors en `DIRECT`), jamais le produit.
 */
export function getProductDeepLink(baseUrl: string, shortLink: string, productId: string): string {
  return `${getStorefrontUrl(baseUrl, shortLink)}/p/${productId}`
}

/**
 * URL publique du récap d'une commande (Palier 1) — PATH durable `/s/<slug>/order/<id>`.
 *
 * `orderId` est un cuid (capability token non-devinable). Même contrat de robustesse que le
 * deep-link produit : path segment (jamais query param) pour survivre au scraper OG WhatsApp
 * et aux proxys transparents Orange/MTN CI.
 */
export function getOrderRecapUrl(baseUrl: string, shortLink: string, orderId: string): string {
  return `${getStorefrontUrl(baseUrl, shortLink)}/order/${orderId}`
}

/**
 * Optimise une URL Cloudinary pour l'aperçu Open Graph scrapé par WhatsApp.
 *
 * 1200x630 (ratio 1.91:1), JPEG (baseline vs progressif indifférent pour le scraper),
 * profil métadonnées strippé, q_auto:eco pour rester < 200 Ko — au-delà, l'aperçu échoue
 * silencieusement sur réseau 3G (constat terrain Afrique de l'Ouest, confirmé dissidence).
 *
 * @param fallbackUrl URL d'image par défaut si `url` est absente.
 */
export function optimizeForOG(url: string | undefined | null, fallbackUrl: string): string {
  if (!url) return fallbackUrl
  if (!url.includes('cloudinary')) return url
  return url.replace(
    '/upload/',
    '/upload/w_1200,h_630,c_fill,g_auto,f_jpg,q_auto:eco,fl_strip_profile/'
  )
}
